import React, { useEffect, useState } from "react";
import { ArrowDown, ChevronRight } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

import { fetchCategories } from "../services/categories.service";
import { fetchProducts } from "../services/products.service";

import Products from "../components/Products";

export default function HomePage({ onNavigate }) {
  const { t, lang } = useI18n();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const langMap = { AR: "ar", EN: "en", TR: "tr" };
    const apiLang = langMap[lang] || "ar";

    Promise.all([
      fetchCategories({ lang: apiLang }),
      fetchProducts({ lang: apiLang }),
    ])
      .then(([cats, prods]) => {
        if (cancelled) return;

        setCategories(Array.isArray(cats) ? cats : []);
        setProducts(Array.isArray(prods) ? prods : []);
      })
      .catch((err) => console.error("HomePage fetch error:", err));

    return () => {
      cancelled = true;
    };
  }, [lang]);

  return (
    <>
      {/* Hero */}
      <header className="relative w-full h-screen overflow-hidden flex items-center justify-center bg-[#121212]">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&w=1920&q=80"
            alt="Luxury Atmosphere"
            className="w-full h-full object-cover opacity-70 animate-slow-scale"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/90" />
        </div>

        <div className="relative z-10 container mx-auto px-6 flex flex-col items-center justify-center text-center h-full pt-20">
          <span className="text-[#c32228] font-bold tracking-[0.5em] text-sm md:text-base uppercase mb-6 border px-4 py-1 border-[#c32228]/30 rounded-full bg-black/20 backdrop-blur-sm">
            {t("hero.tagline")}
          </span>

          <div className="relative mb-12">
            <h1 className="font-serif text-[8rem] md:text-[12rem] leading-none text-white/5 font-black absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 whitespace-nowrap z-0 select-none">
              {t("hero.title")}
            </h1>

            <h1 className="relative z-10 font-serif text-6xl md:text-8xl lg:text-9xl text-white font-bold drop-shadow-2xl">
              {t("hero.title")}
              <span className="block text-2xl md:text-4xl font-sans font-light mt-2 tracking-widest text-gray-300">
                {t("hero.subtitle")}
              </span>
            </h1>
          </div>

          <div className="flex flex-col items-center mt-6">
            <button
              onClick={() => onNavigate("categories")}
              className="group relative px-10 py-5 rounded-full border border-white/40 hover:border-[#c32228] bg-transparent hover:bg-[#c32228] transition-all duration-500 ease-out"
            >
              <span className="flex items-center gap-4 text-white text-lg font-bold tracking-[0.25em] uppercase transition-colors duration-300">
                {t("hero.cta")}
                <ArrowDown
                  size={20}
                  className="text-white group-hover:translate-y-1 transition-transform duration-300"
                />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Categories Preview */}
      <section className="py-24 bg-white relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#c32228] font-bold tracking-widest uppercase text-sm">
              {t("home.categoriesSubtitle")}
            </span>

            <h2 className="text-4xl font-serif text-[#121212] mt-2">
              {t("home.categoriesTitle")}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {categories.slice(0, 3).map((cat, i) => (
              <div
                key={cat.id || i}
                onClick={() => onNavigate("products", cat.id)}
                className="relative h-64 rounded-lg overflow-hidden cursor-pointer group border-2 border-[#c32228]"
              >
                <img
                  src={
                    cat.imageUrl ||
                    "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=600"
                  }
                  alt={cat.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                  <h3 className="text-2xl font-serif font-bold border-b-2 border-transparent group-hover:border-[#c32228] transition-all pb-1 text-white">
                    {cat.name}
                  </h3>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => onNavigate("categories")}
              className="text-[#c32228] font-bold border-b border-[#c32228] pb-1 hover:text-[#8a1c20]"
            >
              {t("home.viewAllCategories")}
            </button>
          </div>
        </div>
      </section>

      {/* Products Preview */}
      <section className="py-24 bg-[#1a0505] text-white relative border-t-8 border-[#c32228]">
        <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/dark-matter.png')]" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="flex justify-between items-end mb-12 border-b border-white/10 pb-6">
            <div>
              <h2 className="text-4xl md:text-5xl font-serif text-white mb-2">
                {t("home.productsTitle")}
              </h2>
              <p className="text-gray-400">
                {t("home.productsSubtitle")}
              </p>
            </div>

            <button
              onClick={() => onNavigate("products")}
              className="text-[#c32228] hover:text-white transition-colors flex items-center gap-2 font-bold"
            >
              {t("home.fullMenu")}
              <ChevronRight size={18} />
            </button>
          </div>

          <Products
            products={products.slice(0, 4)}
            onNavigate={onNavigate}
          />
        </div>
      </section>
    </>
  );
}
